// SocketManager interface - matches the socketManager from useMeeting hook
interface SocketManager {
  sendOffer: (data: { offer: RTCSessionDescriptionInit; targetUserId: string }) => void;
  sendAnswer: (data: { answer: RTCSessionDescriptionInit; targetUserId: string }) => void;
  sendIceCandidate: (data: { candidate: RTCIceCandidate; targetUserId: string }) => void;
  isSocketConnected: () => boolean;
  onOffer: (callback: (data: any) => void) => void;
  onAnswer: (callback: (data: any) => void) => void;
  onIceCandidate: (callback: (data: any) => void) => void;
}

export interface PeerConnectionCallbacks {
  onRemoteStream?: (userId: string, stream: MediaStream) => void;
  onConnectionStateChange?: (userId: string, state: RTCPeerConnectionState) => void;
  onIceConnectionStateChange?: (userId: string, state: RTCIceConnectionState) => void;
  onIceCandidate?: (userId: string, candidate: RTCIceCandidate) => void;
}

export interface CreatePeerConnectionOptions {
  userId: string;
  localStream: MediaStream | null;
  callbacks?: PeerConnectionCallbacks;
  socketManager?: SocketManager;
}

/**
 * Creates a new RTCPeerConnection with configured handlers
 */
export function createPeerConnection({
  userId,
  localStream,
  callbacks = {},
  socketManager
}: CreatePeerConnectionOptions): RTCPeerConnection {
  const peerConnection = new RTCPeerConnection({
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ]
  });

  // Add local stream to peer connection
  if (localStream) {
    localStream.getTracks().forEach(track => {
      peerConnection.addTrack(track, localStream);
    });
  }

  // Handle remote stream
  peerConnection.ontrack = (event) => {
    console.log('🎥 Received remote stream from:', userId);
    const remoteStream = event.streams[0];
    if (remoteStream && callbacks.onRemoteStream) {
      callbacks.onRemoteStream(userId, remoteStream);
    }
  };

  // Handle connection state changes
  peerConnection.onconnectionstatechange = () => {
    console.log('🔗 Connection state changed for', userId, ':', peerConnection.connectionState);
    if (callbacks.onConnectionStateChange) {
      callbacks.onConnectionStateChange(userId, peerConnection.connectionState);
    }
  };

  // Handle ICE connection state changes
  peerConnection.oniceconnectionstatechange = () => {
    console.log('🔗 ICE connection state changed for', userId, ':', peerConnection.iceConnectionState);
    if (callbacks.onIceConnectionStateChange) {
      callbacks.onIceConnectionStateChange(userId, peerConnection.iceConnectionState);
    }
  };

  // Handle ICE candidates
  peerConnection.onicecandidate = (event) => {
    if (event.candidate) {
      if (socketManager) {
        console.log('🔗 Sending ICE candidate via SocketManager to:', userId);
        socketManager.sendIceCandidate({
          candidate: event.candidate,
          targetUserId: userId
        });
      } else if (callbacks.onIceCandidate) {
        callbacks.onIceCandidate(userId, event.candidate);
      }
    }
  };

  return peerConnection;
}

/**
 * Handles incoming SDP offer
 */
export async function handleOffer(
  data: { senderId: string; offer: RTCSessionDescriptionInit },
  peerConnections: Map<string, RTCPeerConnection>,
  createConnection: (userId: string) => RTCPeerConnection,
  socketManager?: SocketManager
): Promise<void> {
  const { senderId, offer } = data;
  console.log('🔗 Handling offer from:', senderId);

  let peerConnection = peerConnections.get(senderId);
  if (!peerConnection) {
    console.log('🔗 Creating new peer connection for:', senderId);
    peerConnection = createConnection(senderId);
    peerConnections.set(senderId, peerConnection);
  }

  try {
    await peerConnection.setRemoteDescription(offer);
    const answer = await peerConnection.createAnswer();
    await peerConnection.setLocalDescription(answer);

    if (socketManager) {
      console.log('🔗 Sending answer via SocketManager to:', senderId);
      socketManager.sendAnswer({
        answer: answer,
        targetUserId: senderId
      });
    }
  } catch (error) {
    console.error('🔗 Error handling offer:', error);
    throw error;
  }
}

/**
 * Handles incoming SDP answer
 */
export async function handleAnswer(
  data: { senderId: string; answer: RTCSessionDescriptionInit },
  peerConnections: Map<string, RTCPeerConnection>
): Promise<void> {
  const { senderId, answer } = data;
  console.log('🔗 Handling answer from:', senderId);

  const peerConnection = peerConnections.get(senderId);
  if (peerConnection) {
    try {
      await peerConnection.setRemoteDescription(answer);
      console.log('🔗 Answer set successfully for:', senderId);
    } catch (error) {
      console.error('🔗 Error handling answer:', error);
      throw error;
    }
  } else {
    console.error('🔗 No peer connection found for:', senderId);
    throw new Error(`No peer connection found for ${senderId}`);
  }
}

/**
 * Handles incoming ICE candidate
 */
export async function handleIceCandidate(
  data: { senderId: string; candidate: RTCIceCandidateInit },
  peerConnections: Map<string, RTCPeerConnection>
): Promise<void> {
  const { senderId, candidate } = data;
  console.log('🔗 Handling ICE candidate from:', senderId);

  const peerConnection = peerConnections.get(senderId);
  if (peerConnection) {
    try {
      await peerConnection.addIceCandidate(candidate);
      console.log('🔗 ICE candidate added successfully for:', senderId);
    } catch (error) {
      console.error('🔗 Error handling ICE candidate:', error);
      throw error;
    }
  } else {
    console.error('🔗 No peer connection found for ICE candidate:', senderId);
    throw new Error(`No peer connection found for ${senderId}`);
  }
}

/**
 * Initiates WebRTC connection with a participant
 */
export async function initiateConnection(
  userId: string,
  selfId: string,
  peerConnections: Map<string, RTCPeerConnection>,
  createConnection: (userId: string) => RTCPeerConnection,
  socketManager?: SocketManager
): Promise<void> {
  if (userId === selfId) {
    console.log('🔗 Skipping self-connection');
    return;
  }

  console.log('🔗 Creating peer connection for:', userId);
  const peerConnection = createConnection(userId);
  peerConnections.set(userId, peerConnection);

  try {
    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);

    if (socketManager) {
      console.log('🔗 Sending offer via SocketManager to:', userId);
      socketManager.sendOffer({
        offer: offer,
        targetUserId: userId
      });
    }
  } catch (error) {
    console.error('🔗 Error creating offer:', error);
    throw error;
  }
}

/**
 * Replaces tracks in peer connection with new stream tracks
 */
export function replacePeerConnectionTracks(
  peerConnections: Map<string, RTCPeerConnection>,
  newStream: MediaStream
): void {
  peerConnections.forEach((pc, userId) => {
    newStream.getTracks().forEach(track => {
      const sender = pc.getSenders().find(s =>
        s.track && s.track.kind === track.kind
      );
      if (sender) {
        sender.replaceTrack(track).catch(err => {
          console.warn(`Failed to replace track for ${userId}:`, err);
        });
      } else {
        pc.addTrack(track, newStream);
      }
    });
  });
}

/**
 * Closes all peer connections
 */
export function closeAllPeerConnections(
  peerConnections: Map<string, RTCPeerConnection>
): void {
  peerConnections.forEach((pc, userId) => {
    console.log('🔗 Closing peer connection for:', userId);
    pc.close();
  });
  peerConnections.clear();
}

